import { isGecko, isStandaloneDisplay } from 'utils/platform';

// Browser Back-button trap.
//
// The app runs on a MemoryRouter, so the browser's own history does not reflect in-app
// navigation. To make the browser Back button drive in-app "back", we keep a small jail of
// sentinel history entries above the app's base entry. Pressing Back consumes one sentinel and
// fires `popstate` (handled by useBrowserBack) instead of unloading the page. React then
// decides what "back" means: an in-app step, a page handler, or leaving.
//
// Two engines, two interventions, two strategies:
//
//   * Chrome (installChromeBackTrap) marks pushState entries created WITHOUT a user gesture as
//     skippable. We beat it by filling the jail AT LOAD: the login keypad lives in a
//     cross-origin iframe, and Chrome propagates the iframe's sticky user activation up to the
//     top frame, which keeps the load-pushed sentinels non-skippable. The jail is refilled only
//     on real gestures (NOT `message`) to stay under Chrome's pushState throttle.
//
//   * Firefox/Gecko (installGeckoBackTrap) is stricter: it SKIPS any Back-target entry that
//     never received a TOP-frame user interaction, and — unlike Chrome — does NOT propagate the
//     cross-origin iframe's activation to the top document. So the Chrome jail is skipped
//     wholesale and Back unloads straight to the previous tab (verified in real Firefox: a
//     single Back jumps a 5-entry history to about:privatebrowsing with no popstate). Firefox
//     needs a genuinely interacted entry as the target — see installGeckoBackTrap.

export const BROWSER_BACK_EVENT = 'in6to:browserback';
const SENTINEL_STATE = { in6to: true };
const JAIL_DEPTH = 3;

export const installBackTrap = (win = window) => {
  // Only in the top-level window. The auth iframe runs this same entry point; its trap would
  // otherwise swallow Back presses meant for the main window.
  if (win.self !== win.top) return;

  // Install once per window. A second install (e.g. dev HMR re-evaluating the module) would
  // register a second popstate listener — Back handled twice (double navigation) — and a second
  // refill doubling the pushState rate.
  if (win.__in6toBackTrapInstalled) return;
  win.__in6toBackTrapInstalled = true;

  // Installed PWA (standalone): don't build the jail. The trap catches Back by keeping sentinel
  // history entries, which forces history.length > 1 — but a panic needs length === 1 to close the
  // window outright (window.close() is refused on a system-launched window with a longer history,
  // and history can't be collapsed back to 1). In a PWA the trap also has nothing to guard: the app
  // is a fresh launch with no page behind it, so Back merely closes the app (pagehide still wipes)
  // instead of unloading to a previous page. So here we leave history a single entry — the native
  // Back closes the PWA, in-app navigation uses the on-screen controls, and the panic close works.
  // A browser tab keeps the trap (there Back really would unload to whatever was before the app).
  if (isStandaloneDisplay(win)) return;

  if (isGecko(win.navigator?.userAgent)) {
    installGeckoBackTrap(win);
    return;
  }

  installChromeBackTrap(win);
};

function installChromeBackTrap(win) {
  let depth = 0;
  // Position of the entry we are currently sitting on, so a popstate can tell a Back (moved to a
  // LOWER sentinel) from a Forward (moved to a HIGHER one). A browser Forward fires `popstate`
  // exactly like a Back, so without this every Forward was mis-handled as another in-app Back —
  // the reported bug: open chat -> Back -> Forward signed the user out to login instead of
  // returning to the chat. `depth` still drives the refill; `pos` only encodes direction. In real
  // navigation they move together (each sentinel is one step); they only diverge under the
  // synthetic bare-popstate unit fixtures, which never exercise Forward.
  let pos = 0;

  const refill = () => {
    while (depth < JAIL_DEPTH) {
      depth += 1;
      pos += 1;
      win.history.pushState({ ...SENTINEL_STATE, pos }, '');
    }
  };

  refill();

  // Top up on real top-window gestures (in-app navigation taps). Deliberately NOT `message`:
  // the postMessage flood would push on every Back and spike pushState into Chrome's throttle.
  const onGesture = () => refill();
  for (const type of ['pointerdown', 'keydown', 'touchstart', 'visibilitychange']) {
    win.addEventListener(type, onGesture, { capture: true });
  }

  win.addEventListener('popstate', (event) => {
    const targetPos = event?.state?.pos ?? 0;
    const direction = targetPos > pos ? 'forward' : 'back';
    pos = targetPos;

    // Forward re-enters a sentinel we had stepped off, so it tops the jail back up; Back consumes
    // one. No re-arm here: a pushState outside a user gesture would poison the entry below and
    // break the next Back. The load-filled, gesture-refilled jail absorbs repeats.
    if (direction === 'forward') depth += 1;
    else if (depth > 0) depth -= 1;

    win.dispatchEvent(new CustomEvent(BROWSER_BACK_EVENT, { detail: { direction } }));
  });
}

// Firefox/Gecko Back-button trap. See the header note for why the Chrome jail is skipped here.
//
// Firefox refuses to skip a Back-target entry only once the top document has user activation: a
// sentinel pushed while the top frame is activated becomes a valid Back target, one pushed
// before (e.g. the load-filled jail) stays skippable. Crucially — verified in real Firefox —
// activation propagates from the cross-origin login iframe to the top document
// (navigator.userActivation.hasBeenActive === true) even though NO top-frame DOM gesture fires.
//
// Mechanism:
//   * Bootstrap — push the first sentinel as soon as the top frame is activated. Two triggers:
//       - a real top-frame gesture (in-app tap), which activates immediately; and
//       - hasBeenActive flipping true via the iframe login, polled because there is no event for
//         it. This is what lets a Back pressed right after login — before any in-app tap — be
//         trapped instead of unloading to the previous tab.
//   * Keep it live — on every popstate we re-push a sentinel above the entry we landed on, so
//     the NEXT Back lands on a valid target again and fires popstate again. This is what makes
//     several Back presses in a row each drive one in-app step via useBrowserBack. Re-pushing on
//     popstate is SAFE on Firefox (it skips by missing activation, not by Chrome's "a no-gesture
//     push poisons the entry below" rule) and the document stays activated (sticky).
//
// Before ANY interaction (hasBeenActive false — e.g. Back pressed on the bare login screen
// without ever touching the keypad) there is nothing to trap and Back leaves the app. That is
// acceptable: nothing sensitive is shown yet, and the forward-return guard still arms on
// pagehide so the app can't be re-reached via Forward.
const ACTIVATION_POLL_MS = 200;

function installGeckoBackTrap(win) {
  let bootstrapped = false;
  let activationPoll = null;

  const arm = () => win.history.pushState(SENTINEL_STATE, '');

  const bootstrap = () => {
    if (bootstrapped) return;
    bootstrapped = true;
    if (activationPoll !== null) clearInterval(activationPoll);
    arm();
  };

  // Top-frame gesture: activates immediately, bootstrap now.
  const onGesture = () => bootstrap();
  for (const type of ['pointerdown', 'keydown', 'touchstart']) {
    win.addEventListener(type, onGesture, { capture: true });
  }

  // Cross-origin iframe login: no top-frame gesture, but it still grants the top document sticky
  // activation. Poll for it (cheap property read; self-clears on bootstrap).
  activationPoll = setInterval(() => {
    if (win.navigator?.userActivation?.hasBeenActive) bootstrap();
  }, ACTIVATION_POLL_MS);

  win.addEventListener('popstate', () => {
    // Always 'back': re-arming here pushes a fresh sentinel that truncates the forward entry, so
    // a browser Forward can never produce a popstate on Gecko (no Forward-as-Back bug to guard).
    // The direction is still tagged so useBrowserBack sees the same event shape on both engines.
    arm();
    win.dispatchEvent(new CustomEvent(BROWSER_BACK_EVENT, { detail: { direction: 'back' } }));
  });
}
